import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import TaskCard from './TaskCard';
import websocketTaskService from '../services/websocketTaskService';
// Removed mockTaskService import

const Trigger = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  background: ${({ theme }) => theme.primary || '#1a73e8'};
  color: white;
  padding: 12px 20px;
  border-radius: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  z-index: 9999;
  transition: all 0.3s ease;
  user-select: none;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    transform: translateY(-2px);
  }
`;

const Spinner = styled.span`
  animation: spin 1s linear infinite;
  display: inline-block;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Panel = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 400px;
  max-height: 500px;
  background: ${({ theme }) => theme.card || '#1e1e1e'};
  border: 1px solid ${({ theme }) => theme.border || '#333'};
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  z-index: 9999;
  animation: slideUp 0.3s ease;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    bottom: 0;
    right: 0;
    left: 0;
    width: 100%;
    max-height: 70vh;
    border-radius: 12px 12px 0 0;
  }

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const Header = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.border || '#333'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.card2 || '#252526'};
  flex-shrink: 0;
`;

const Title = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text || '#fff'};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.text || '#fff'};
  opacity: 0.7;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.buttonHover || 'rgba(255,255,255,0.1)'};
    opacity: 1;
  }
`;

const Body = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
  flex: 1;
  background: ${({ theme }) => theme.card || '#1e1e1e'};

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.border || '#333'};
    border-radius: 3px;
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${({ theme }) => theme.text || '#fff'};
  opacity: 0.5;
  font-size: 14px;
`;

const TaskNotificationManager = () => {
  const [tasks, setTasks] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const panelRef = useRef(null);
  const prevTaskCountRef = useRef(0);

  useEffect(() => {
    // Initial connection
    websocketTaskService.connect();
    
    // Subscribe to updates
    const unsubscribe = websocketTaskService.subscribe((updatedTasks) => {
      setTasks([...updatedTasks]);
    });
    
    // Auto-cleanup finished tasks logic is now handled in service or effect?
    // Let's keep a simple cleanup here for visual removal from list if specific requirement,
    // otherwise let user clear them.
    // The previous requirement was "Auto-remove completed tasks after 5 seconds"
    const cleanupInterval = setInterval(() => {
        const currentTasks = websocketTaskService.getTasks();
        const now = Date.now();
        let changed = false;
        
        currentTasks.forEach(task => {
            if (task.status === 'completed' && task.completedAt) {
                const completedTime = new Date(task.completedAt).getTime();
                if (now - completedTime > 5000) {
                    websocketTaskService.removeTask(task.operationId);
                    changed = true;
                }
            }
        });
    }, 1000);

    return () => {
      unsubscribe();
      clearInterval(cleanupInterval);
    };
  }, []);

  // Auto-expand when new task is added
  useEffect(() => {
    const currentTaskCount = tasks.length;
    const prevTaskCount = prevTaskCountRef.current;
    
    // If task count increased, auto-expand
    if (currentTaskCount > prevTaskCount && currentTaskCount > 0) {
      setIsExpanded(true);
    }
    
    // Update ref for next comparison
    prevTaskCountRef.current = currentTaskCount;
  }, [tasks.length]);

  // No more simulation logic needed


  const activeCount = tasks.filter(t => t.status === 'running').length;
  const hasActiveTasks = activeCount > 0;

  const handleClickOutside = (e) => {
    if (panelRef.current && !panelRef.current.contains(e.target)) {
      setIsExpanded(false);
    }
  };

  useEffect(() => {
    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded]);

  // DEBUG: Commented out to ensure component renders
  // if (!hasActiveTasks && tasks.length === 0) {
  //   return null;
  // }
  
  // TaskNotificationManager rendering

  return (
    <>
      {!isExpanded && activeCount > 0 && (
        <Trigger onClick={() => setIsExpanded(true)}>
          <Spinner>⟳</Spinner>
          <span style={{ flex: 1 }}>
            {activeCount} task{activeCount !== 1 ? 's' : ''} running
          </span>
          <span style={{ fontSize: '10px', opacity: 0.8 }}>▼</span>
        </Trigger>
      )}

      {isExpanded && (
        <Panel ref={panelRef}>
          <Header>
            <Title>Tasks ({tasks.length})</Title>
            <Actions>
              <ActionButton onClick={() => setIsExpanded(false)} title="Minimize">
                ▼
              </ActionButton>
              <ActionButton 
                onClick={() => {
                  websocketTaskService.clearAllTasks();
                  // State update handled by subscription
                  setIsExpanded(false);
                }} 
                title="Clear all"
              >
                ✕
              </ActionButton>
            </Actions>
          </Header>
          
          <Body>
            {tasks.length === 0 ? (
              <EmptyMessage>No tasks</EmptyMessage>
            ) : (
              tasks.map(task => (
                <TaskCard key={task.operationId} task={task} />
              ))
            )}
          </Body>
        </Panel>
      )}
    </>
  );
};

export default TaskNotificationManager;
